import React from 'react';
import { cn } from '@lqa-frontend/ui-system';
import { ICON_REGISTRY } from './icon-registry';
import type { IconName } from './icon-registry';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Allowed icon sizes from the Figma design spec.
 *
 * | Token | Pixels | Use case                              |
 * |-------|--------|---------------------------------------|
 * | `xs`  | 12px   | Tight spaces, inline indicators       |
 * | `s`   | 16px   | Default, inline text, compact UI      |
 * | `m`   | 20px   | Medium emphasis, form controls        |
 * | `l`   | 24px   | Large emphasis, headings, standalone  |
 */
export type IconSize = 'xs' | 's' | 'm' | 'l';

/** Maps size tokens to pixel values for the SVG width/height attributes. */
const ICON_SIZE_PX: Record<IconSize, number> = {
	xs: 12,
	s: 16,
	m: 20,
	l: 24,
};

export type IconProps = {
	/** Icon to render — type-safe union of all 215 available icon names. */
	'name': IconName;

	/**
	 * Icon size.
	 * @default 's'
	 */
	'size'?: IconSize;

	/** Additional CSS classes (e.g. for color overrides via `text-*`). */
	'className'?: string;

	/** Accessible label. When provided, sets `role="img"` + `aria-label`. */
	'aria-label'?: string;

	/** Test identifier. */
	'data-testid'?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `Icon` — Renders an SVG icon from the LQA Design System icon library.
 *
 * All 215 icons are available via the type-safe `name` prop with full
 * autocompletion. Icons use `currentColor` for fill/stroke, so they
 * inherit the parent element's text color.
 *
 * Each icon is an individual SVG file imported as a React component via SVGR.
 *
 * ```tsx
 * <Icon name="check" />
 * <Icon name="chevronDown" size="m" />
 * <Icon name="trash" size="l" className="text-text-danger" />
 * ```
 *
 * For icons inside colored background containers, use the `AccentIcon`
 * component instead.
 */
export function Icon({
	name,
	size = 's',
	className,
	'aria-label': ariaLabel,
	'data-testid': testId,
}: IconProps) {
	const SvgComponent = ICON_REGISTRY[name];

	if (!SvgComponent) {
		if (process.env.NODE_ENV === 'development') {
			console.warn(`Icon: unknown icon name "${name}"`);
		}

		return null;
	}

	const px = ICON_SIZE_PX[size];

	return (
		<SvgComponent
			width={px}
			height={px}
			className={cn('inline-flex shrink-0', className)}
			role={ariaLabel ? 'img' : undefined}
			aria-label={ariaLabel}
			aria-hidden={ariaLabel ? undefined : true}
			data-testid={testId}
		/>
	);
}
